import type { ReactElement } from 'react';
import { Badge, type BadgeTone } from '@/components/ui';
import { cx } from '@/lib/cx';
import type { StageSummary } from '@/lib/types';

/*
 * The deal-stage colour swatch, and the one place its hex is validated.
 *
 * `d_deal_stages.colour` is admin-editable per-tenant data, not a design
 * token, so it cannot be assumed to contrast with either theme:
 *
 * - No text is ever put ON it — it renders as a small square beside a label
 *   that already reads correctly without it, with a `hairline-strong` border
 *   so it stays visible on any surface in either theme. No `dark:` variant
 *   needed — the token swaps itself.
 * - Colour is never the only signal — the stage NAME is always shown beside
 *   it and the swatch is `aria-hidden`; ignoring it loses no information.
 * - It is unvalidated text over the wire, so it is checked before reaching a
 *   `style`: only `#` plus exactly six hex digits is accepted, anything else
 *   (`#abc`, `red`, `rgb(...)`, empty, a number, `null`, absent) renders NO
 *   swatch — never a grey/black placeholder, which would imply a colour the
 *   stage does not have.
 *
 * `colour` is typed `unknown` on purpose: present-and-nullable on some
 * records, absent on others, and every case collapses to the same validator
 * here.
 *
 * Exports are components only — a second export would break fast refresh.
 */

/** `#rrggbb`. The contract stores lower-case; a tolerant read costs nothing. */
const HEX_SWATCH = /^#[0-9a-f]{6}$/i;

/**
 * The validated colour, or null when there is nothing safe to paint.
 *
 * Deliberately not exported: one validator, used by the two components below,
 * so a call site cannot acquire a hex this file has not checked.
 */
function swatchHex(colour: unknown): string | null {
  if (typeof colour !== 'string') return null;
  const trimmed = colour.trim();
  return HEX_SWATCH.test(trimmed) ? trimmed.toLowerCase() : null;
}

export interface StageSwatchProps {
  /** The raw value off the record. Anything but a valid `#rrggbb` renders nothing. */
  colour: unknown;
  className?: string;
}

/**
 * A stage's colour, as a square beside its name.
 *
 * A square rather than a circle, so it does not read as the `Badge` status dot
 * it sits in place of: a dot means "this state has a tone", a swatch means
 * "this is the stage's own colour".
 */
export function StageSwatch({ colour, className }: StageSwatchProps): ReactElement | null {
  const hex = swatchHex(colour);
  if (hex === null) return null;
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-block h-2.5 w-2.5 shrink-0 rounded-[3px] border border-hairline-strong',
        className,
      )}
      // Validated above, and set through the CSSOM rather than a style string —
      // an unexpected value can neither inject nor produce a broken attribute.
      style={{ backgroundColor: hex }}
    />
  );
}

/**
 * A deal's stage pill: swatch, name, and the probability expected revenue uses.
 *
 * `isWon` and `isLost` are independent flags, not a state machine — read in a
 * fixed order and neither is assumed set. The leading square takes the status
 * dot's place only when the stage carries a usable colour; otherwise the dot
 * renders as usual. Never a placeholder swatch for a stage with no colour.
 */
export function DealStageBadge({ stage }: { stage: StageSummary | null }): ReactElement {
  if (!stage) return <span className="text-xs text-fg-subtle">No stage</span>;

  /*
   * `StageSummary` does not declare `colour` — `deal.stage.colour` arrives on
   * the wire and `types.ts` documents it on `DealStage`. Reading it as an
   * optional unknown key means this works whether or not the summary type has
   * caught up, and never asserts a shape the response might not have.
   */
  const hex = swatchHex((stage as StageSummary & { colour?: unknown }).colour);
  const tone: BadgeTone = stage.isWon ? 'success' : stage.isLost ? 'danger' : 'info';

  return (
    <Badge
      tone={tone}
      dot={hex === null}
      icon={hex === null ? undefined : <StageSwatch colour={hex} />}
    >
      <span className="truncate">{stage.name}</span>
      <span className="opacity-70 tabular">{stage.probability}%</span>
    </Badge>
  );
}
